import imageCompression from 'browser-image-compression';

export type MediaKind = 'image' | 'video' | 'pdf' | 'other';

export interface CompressResult {
  uploadFile: File;
  namingFile: File | null;
  originalSize: number;
  finalSize: number;
  wasCompressed: boolean;
  withinLimit: boolean;
  reason?: string;
}

export const WHATSAPP_LIMITS: Record<MediaKind, number> = {
  image: 16 * 1024 * 1024,
  video: 16 * 1024 * 1024,
  pdf: 100 * 1024 * 1024,
  other: 100 * 1024 * 1024,
};

export const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif']);
export const VIDEO_EXTS = new Set(['mp4', '3gp', 'mov']);
const PDF_EXTS = new Set(['pdf']);

export const getMediaKind = (file: File): MediaKind => {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (file.type.startsWith('image/') || IMAGE_EXTS.has(ext)) return 'image';
  if (file.type.startsWith('video/') || VIDEO_EXTS.has(ext)) return 'video';
  if (file.type === 'application/pdf' || PDF_EXTS.has(ext)) return 'pdf';
  return 'other';
};

export const humanReadableSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const compressMedia = async (
  file: File,
  customLimitBytes?: number,
  onProgress?: (pct: number) => void
): Promise<CompressResult> => {
  const kind = getMediaKind(file);
  const limit = customLimitBytes ?? WHATSAPP_LIMITS[kind];

  if (kind === 'image' && file.type !== 'image/gif') {
    let uploadFile = file;
    let wasCompressed = false;

    if (file.size > limit) {
      uploadFile = await imageCompression(file, {
        maxSizeMB: (limit / (1024 * 1024)) * 0.9,
        maxWidthOrHeight: 4096,
        initialQuality: 0.82,
        useWebWorker: true,
        onProgress,
      });
      wasCompressed = true;
    }

    const namingFile = await imageCompression(file, {
      maxSizeMB: 1,
      maxWidthOrHeight: 1024,
      initialQuality: 0.7,
      useWebWorker: true,
    });

    return {
      uploadFile,
      namingFile,
      originalSize: file.size,
      finalSize: uploadFile.size,
      wasCompressed,
      withinLimit: uploadFile.size <= limit,
      reason: uploadFile.size > limit ? 'IMAGE_STILL_TOO_LARGE' : undefined,
    };
  }

  return {
    uploadFile: file,
    namingFile: null,
    originalSize: file.size,
    finalSize: file.size,
    wasCompressed: false,
    withinLimit: file.size <= limit,
    reason: file.size > limit ? `${kind.toUpperCase()}_TOO_LARGE` : undefined,
  };
};
